import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { publicOrigin } from "@/lib/publicUrl";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound, Lock, Copy, Sparkles, Loader2, TrendingUp, Coins, Key, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const { user, refreshBalance } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [affiliate, setAffiliate] = useState(null);

  useEffect(() => {
    api.get("/affiliate/summary").then((r) => setAffiliate(r.data)).catch(() => {});
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name });
      toast.success("Profile saved");
      refreshBalance(); // will refresh /auth/me next mount
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  const changePw = async () => {
    if (newPw !== newPw2) return toast.error("New passwords don't match");
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      setOldPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingPw(false); }
  };

  const copyReferral = async () => {
    const url = `${publicOrigin()}/signup?ref=${user?.referral_code}`;
    await navigator.clipboard.writeText(url);
    toast.success("Referral link copied");
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1000px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">ACCOUNT · SETTINGS</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Your <span className="font-bold text-amber-400">account.</span>
        </h1>
      </div>

      <div className="space-y-6">
        {/* Profile */}
        <section className="bg-[#121214] border border-white/5 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-5">
            <UserRound className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
            <div className="font-heading font-bold tracking-tight">Profile</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">NAME</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                data-testid="profile-name"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">EMAIL</Label>
              <Input value={user?.email || ""} readOnly disabled
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11 opacity-60" />
            </div>
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile"
            className="mt-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save profile
          </Button>
        </section>

        {/* Password */}
        <section className="bg-[#121214] border border-white/5 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
            <div className="font-heading font-bold tracking-tight">Change password</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">CURRENT PASSWORD</Label>
              <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
                data-testid="pw-old"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">NEW PASSWORD</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                data-testid="pw-new"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">CONFIRM NEW</Label>
              <Input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                data-testid="pw-new2"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
          </div>
          <Button onClick={changePw} disabled={savingPw || !oldPw || !newPw} data-testid="save-password"
            className="mt-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Update password
          </Button>
        </section>

        {/* Referral */}
        <section className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
            <div className="font-heading font-bold tracking-tight">Your referral link</div>
          </div>
          <p className="text-sm text-zinc-400 mb-3">
            Share this link — both of you get bonus credits when your friend signs up.
          </p>
          <div className="flex gap-2">
            <Input readOnly value={`${publicOrigin()}/signup?ref=${user?.referral_code || ""}`}
              className="bg-[#0b0b0d] border-white/10 font-mono-tech text-xs" />
            <Button onClick={copyReferral} data-testid="copy-referral-settings"
              className="bg-amber-500 hover:bg-amber-600 text-black">
              <Copy className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </section>

        {/* Affiliate credits */}
        {affiliate && (
          <section className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="affiliate-section">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
              <div className="font-heading font-bold tracking-tight">Referral rewards</div>
              <span className="ml-auto font-mono-tech text-[10px] text-zinc-500">
                Earn {affiliate.subscription_reward_pct}% of your referral&apos;s subscription credits · every renewal
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
              <div className="bg-[#0b0b0d] border border-white/5 rounded-md p-3">
                <div className="font-mono-tech text-[9px] text-zinc-500">TOTAL · CREDITS · EARNED</div>
                <div className="font-heading text-2xl font-bold tracking-tight text-emerald-400 mt-1 flex items-center gap-1.5">
                  <Coins className="w-4 h-4" strokeWidth={1.5} />
                  {affiliate.total_credits_earned?.toLocaleString() || 0}
                </div>
              </div>
              <div className="bg-[#0b0b0d] border border-white/5 rounded-md p-3">
                <div className="font-mono-tech text-[9px] text-zinc-500">REFERRALS</div>
                <div className="font-heading text-2xl font-bold tracking-tight text-white mt-1">
                  {affiliate.referred_users_count}
                </div>
              </div>
              <div className="bg-[#0b0b0d] border border-white/5 rounded-md p-3">
                <div className="font-mono-tech text-[9px] text-zinc-500">PAYING · REFERRALS</div>
                <div className="font-heading text-2xl font-bold tracking-tight text-amber-400 mt-1">
                  {affiliate.active_paying_referrals}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-md">
              <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="text-xs text-zinc-300">
                Credits are added <b>instantly</b> to your balance every time a referral pays a subscription invoice.
                No payouts, no paperwork — pure content-generation fuel.
              </div>
            </div>
            {affiliate.transactions.length > 0 && (
              <div className="mt-5 space-y-1.5 max-h-64 overflow-y-auto">
                <div className="font-mono-tech text-[9px] text-zinc-500 mb-2">RECENT · REWARDS</div>
                {affiliate.transactions.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2 border-b border-white/5 text-xs" data-testid={`aff-tx-${t.id}`}>
                    <div className="flex-1">
                      <div className="text-zinc-300">{t.note || "Referral reward"}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {new Date(t.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="font-mono-tech font-bold text-emerald-400 flex items-center gap-1">
                      <Coins className="w-3 h-3" strokeWidth={1.5} />
                      +{(t.credits_awarded || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <ApiKeysSection />
      </div>
    </div>
  );
}


/* ---------- API Keys section (Studio+) ---------- */
const STUDIO_PLUS = ["studio", "enterprise"];

function ApiKeysSection() {
  const { user, isOwner } = useAuth();
  const canUse = isOwner || STUDIO_PLUS.includes(user?.tier);
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null);  // {id, name, raw_key}

  const load = async () => {
    try {
      const { data } = await api.get("/keys");
      setKeys(data);
    } catch (err) {
      if (err?.response?.status === 402) setKeys([]);
    }
  };
  useEffect(() => { if (canUse) load(); }, [canUse]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/keys", { name });
      setFreshKey({ id: data.id, name: data.name, raw_key: data.raw_key });
      setName("");
      load();
      // auto-copy to clipboard
      try { await navigator.clipboard.writeText(data.raw_key); toast.success("Key copied to clipboard"); }
      catch { toast.success("Key created"); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Anything using it will stop working immediately.")) return;
    try {
      await api.delete(`/keys/${id}`);
      toast.success("Key revoked");
      if (freshKey?.id === id) setFreshKey(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <section data-testid="api-keys-section" className="pt-8 border-t border-white/5">
      <div className="flex items-start gap-3 mb-4">
        <Key className="w-5 h-5 text-amber-400 mt-1" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-mono-tech text-[10px] text-amber-400">API · KEYS</div>
          <div className="font-heading font-bold tracking-tight text-lg">Programmatic access</div>
          <div className="text-xs text-zinc-500 mt-1">
            Generate a bearer key to call the MediaOthers API from your own scripts, servers or Zapier zaps.
            Include it as <code className="text-amber-400">Authorization: Bearer sk_mo_...</code> on any /api endpoint.
            {" "}
            <a href="/docs/api" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 underline underline-offset-2" data-testid="docs-link-from-settings">
              Read the docs →
            </a>
          </div>
        </div>
        {!canUse && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono-tech text-zinc-500 px-2 py-1 rounded border border-white/10">
            <Lock className="w-3 h-3" strokeWidth={1.75} /> STUDIO+ ONLY
          </div>
        )}
      </div>

      {!canUse ? (
        <div className="text-xs text-zinc-500 mt-3">
          API access unlocks on the Studio plan. Manage keys, revoke them any time, and run bulk generations
          straight from your own tools.
        </div>
      ) : (
        <>
          {/* Fresh-key reveal — shown once */}
          {freshKey && (
            <div className="mb-4 p-4 rounded-lg border border-emerald-500/40 bg-emerald-500/5" data-testid="fresh-key-banner">
              <div className="font-mono-tech text-[10px] text-emerald-400 mb-2">SAVE · THIS · KEY · NOW — WE WILL NEVER SHOW IT AGAIN</div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="fresh-key-value"
                  className="flex-1 font-mono text-xs bg-[#0b0b0d] p-3 rounded border border-white/10 break-all normal-case tracking-normal"
                >{freshKey.raw_key}</code>
                <Button
                  data-testid="copy-fresh-key"
                  onClick={async () => { try { await navigator.clipboard.writeText(freshKey.raw_key); toast.success("Copied"); } catch { toast.error("Could not copy"); } }}
                  variant="outline"
                  size="sm"
                  className="border-white/10 hover:bg-white/5"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">Key label: {freshKey.name}</div>
            </div>
          )}

          {/* Create form */}
          <form onSubmit={create} className="flex gap-2 mb-4">
            <Input
              data-testid="api-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Key label (e.g. Production server)"
              className="flex-1 bg-[#0b0b0d] border-white/10 h-10"
              maxLength={60}
            />
            <Button
              type="submit"
              disabled={busy || !name.trim()}
              data-testid="create-api-key"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <><Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Create key</>}
            </Button>
          </form>

          {/* List */}
          {keys.length === 0 ? (
            <div className="text-xs text-zinc-500 py-6 text-center border border-dashed border-white/5 rounded-lg">
              No API keys yet — create your first key above.
            </div>
          ) : (
            <div className="space-y-2" data-testid="api-keys-list">
              {keys.map((k) => (
                <div
                  key={k.id}
                  data-testid={`api-key-${k.id}`}
                  className="flex items-center gap-3 p-3 rounded bg-[#0b0b0d] border border-white/5"
                >
                  <Key className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{k.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono normal-case tracking-normal">
                      {k.prefix}…{" · "}
                      created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && <> · last used {new Date(k.last_used_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <button
                    onClick={() => revoke(k.id)}
                    data-testid={`revoke-${k.id}`}
                    title="Revoke"
                    className="text-zinc-500 hover:text-red-400 p-2"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
